import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnalyticsTracker } from '@/hooks/useAnalyticsTracker';

declare global {
  interface Window {
    gtag: (command: string, ...args: any[]) => void;
  }
}

const GA_TRACKING_ID = 'G-70VD841SNR';

// Page name mapping for cleaner analytics reporting
const getPageName = (pathname: string): string => {
  const pageMap: Record<string, string> = {
    '/': 'Home',
    '/competitive-analysis': 'Competitive Analysis',
    '/unlisted': 'Documentation',
    '/newsletter': 'Newsletter',
    '/blog': 'Blog',
    '/auth': 'Authentication',
    '/admin': 'Admin Dashboard',
    '/texas-privacy': 'Texas Privacy',
    '/7-core-segments': '7 Core Segments',
    '/workflow-builder': 'Workflow Builder',
    '/workflow-story': 'Workflow Story',
    '/where-the-data-comes-from': 'Data Source',
    '/how-intent-data-is-gathered': 'Intent Data',
    '/data-source': 'Data Source',
    '/intent': 'Intent Data',
    '/terms-of-service': 'Terms of Service',
    '/contact': 'Contact',
    '/privacy-policy': 'Privacy Policy',
    '/ccpa-privacy': 'CCPA Privacy',
    '/cookie-policy': 'Cookie Policy',
    '/dpa': 'Data Processing Agreement',
    '/opt-out': 'Opt Out',
    '/acceptable-use': 'Acceptable Use Policy',
    '/sla': 'Service Level Agreement',
    '/gdpr-rights': 'GDPR Rights',
    '/disclaimer': 'Disclaimer',
    '/refund-policy': 'Refund Policy',
    '/pricing': 'Pricing',
    '/analytics': 'Analytics Dashboard',
    '/facebook-lookalike-audiences': 'Facebook Lookalike Guide',
  };

  // Handle dynamic blog post routes
  if (pathname.startsWith('/blog/')) {
    return 'Blog Post';
  }

  return pageMap[pathname] || 'Unknown Page';
};

export const GoogleAnalytics = () => {
  const location = useLocation();
  
  // Track to local database
  useAnalyticsTracker();

  useEffect(() => {
    // Small delay to ensure document.title is updated
    const timeoutId = setTimeout(() => {
      if (typeof window.gtag === 'function') {
        const pageName = getPageName(location.pathname);
        
        // Send page_view event with enhanced data
        window.gtag('config', GA_TRACKING_ID, {
          page_path: location.pathname + location.search,
          page_title: document.title,
          page_location: window.location.href,
        });

        // Also send as a custom event for better tracking visibility
        window.gtag('event', 'page_view', {
          page_name: pageName,
          page_path: location.pathname,
          page_title: document.title,
          page_referrer: document.referrer,
        });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location]);

  return null;
};
